export const ROOM_TYPES = [
    'Hall',
    'Garden',
    'Pool Area',
    'Beach',
    'Conference Room',
    'Shul',
    'Ballroom',
    'Tent',
    'Rooftop',
    'Courtyard',
];
/**
 * Calculate chair positions around a round table.
 * Returns positions in feet relative to table center.
 */
export function calculateRoundTableChairs(tableWidthFt, seatCount, chairSizeFt = 1.5) {
    const radius = tableWidthFt / 2 + chairSizeFt * 0.7;
    const positions = [];
    for (let i = 0; i < seatCount; i++) {
        const angle = (2 * Math.PI * i) / seatCount - Math.PI / 2;
        positions.push({
            x: radius * Math.cos(angle),
            y: radius * Math.sin(angle),
            rotation: ((angle + Math.PI / 2) * 180) / Math.PI,
        });
    }
    return positions;
}
export function calculateRectTableChairs(tableWidthFt, tableHeightFt, seatCount, chairSizeFt = 1.5, sides = 'top-bottom') {
    const positions = [];
    const gap = 0.15; // ~2 inches gap between chair and table edge
    const topY = -(chairSizeFt + gap);
    const bottomY = tableHeightFt + gap;
    const leftX = -(chairSizeFt + gap);
    const rightX = tableWidthFt + gap;
    // Determine which sides get chairs
    const hasTop = sides === 'all' || sides === 'top' || sides === 'top-bottom';
    const hasBottom = sides === 'all' || sides === 'bottom' || sides === 'top-bottom';
    const hasLeft = sides === 'all' || sides === 'left' || sides === 'left-right';
    const hasRight = sides === 'all' || sides === 'right' || sides === 'left-right';
    const sideCount = [hasTop, hasBottom, hasLeft, hasRight].filter(Boolean).length;
    if (sideCount === 0)
        return positions;
    const perSide = Math.ceil(seatCount / sideCount);
    // Helper: distribute N chairs evenly along an edge of length edgeFt
    // Returns center positions with equal gaps between chairs and from edges
    const distribute = (n, edgeFt) => {
        if (n <= 0)
            return [];
        if (n === 1)
            return [edgeFt / 2 - chairSizeFt / 2];
        // Total space needed: n chairs + (n-1) gaps between them
        // gap = (edgeFt - n * chairSizeFt) / (n + 1) for equal edge margins
        const gap = (edgeFt - n * chairSizeFt) / (n + 1);
        const effectiveGap = Math.max(gap, 0); // prevent negative
        return Array.from({ length: n }, (_, i) => effectiveGap + i * (chairSizeFt + effectiveGap));
    };
    if (hasTop) {
        const xs = distribute(perSide, tableWidthFt);
        for (let i = 0; i < xs.length && positions.length < seatCount; i++) {
            positions.push({ x: xs[i], y: topY, rotation: 0 });
        }
    }
    if (hasBottom) {
        const xs = distribute(perSide, tableWidthFt);
        for (let i = 0; i < xs.length && positions.length < seatCount; i++) {
            positions.push({ x: xs[i], y: bottomY, rotation: 180 });
        }
    }
    if (hasLeft) {
        const ys = distribute(perSide, tableHeightFt);
        for (let i = 0; i < ys.length && positions.length < seatCount; i++) {
            positions.push({ x: leftX, y: ys[i], rotation: 270 });
        }
    }
    if (hasRight) {
        const ys = distribute(perSide, tableHeightFt);
        for (let i = 0; i < ys.length && positions.length < seatCount; i++) {
            positions.push({ x: rightX, y: ys[i], rotation: 90 });
        }
    }
    return positions;
}
/**
 * Calculate chair row group dimensions and positions.
 */
export function calculateChairRowGroup(rows, seatsPerRow, chairSpacingFt = 2, rowSpacingFt = 3, chairSizeFt = 1.5) {
    const totalWidthFt = seatsPerRow * chairSpacingFt;
    const totalHeightFt = rows * rowSpacingFt;
    const chairs = [];
    for (let r = 0; r < rows; r++) {
        for (let s = 0; s < seatsPerRow; s++) {
            chairs.push({
                x: s * chairSpacingFt + (chairSpacingFt - chairSizeFt) / 2,
                y: r * rowSpacingFt + (rowSpacingFt - chairSizeFt) / 2,
                rotation: 0,
            });
        }
    }
    return { totalWidthFt, totalHeightFt, chairs };
}
/**
 * Calculate total footprint of a table with chairs in feet.
 */
export function calculateTableFootprint(tableWidthFt, tableHeightFt, isRound, seatCount, chairSizeFt = 1.5) {
    if (isRound) {
        const radius = tableWidthFt / 2 + chairSizeFt * 0.7 + chairSizeFt;
        return { widthFt: radius * 2, heightFt: radius * 2 };
    }
    return {
        widthFt: tableWidthFt + (seatCount > 0 ? 0 : 0),
        heightFt: tableHeightFt + (seatCount > 0 ? chairSizeFt * 2 : 0),
    };
}
/**
 * Calculate distance between two points in feet.
 */
export function calculateDistanceFt(x1, y1, x2, y2) {
    return Math.sqrt((x2 - x1) ** 2 + (y2 - y1) ** 2);
}
export const TABLE_GROUP_COLORS = {
    bride: '#f9a8d4',
    groom: '#93c5fd',
    vip: '#fcd34d',
    family: '#86efac',
    general: '#d1d5db',
};
//# sourceMappingURL=floor-plan.js.map